/**
 * Safe storage: tries localStorage first, falls back to in-memory when unavailable.
 * Use in webview/mobile contexts where localStorage may be disabled or throw.
 * In-memory storage lasts for the page session only.
 */

const memoryStore: Record<string, string> = {};
let localStorageAvailable: boolean | null = null;

function isLocalStorageAvailable(): boolean {
  if (localStorageAvailable !== null) return localStorageAvailable;
  try {
    const key = '__acan_storage_test__';
    localStorage.setItem(key, '1');
    localStorage.removeItem(key);
    localStorageAvailable = true;
  } catch {
    localStorageAvailable = false;
  }
  return localStorageAvailable;
}

export function getItem(key: string): string | null {
  try {
    if (isLocalStorageAvailable()) {
      return localStorage.getItem(key);
    }
  } catch {
    // fall through to memory
  }
  return key in memoryStore ? memoryStore[key] : null;
}

export function setItem(key: string, value: string): void {
  try {
    if (isLocalStorageAvailable()) {
      localStorage.setItem(key, value);
      return;
    }
  } catch {
    // fall through to memory
  }
  memoryStore[key] = value;
}

export function removeItem(key: string): void {
  try {
    if (isLocalStorageAvailable()) {
      localStorage.removeItem(key);
    }
  } catch {
    // ignore
  }
  delete memoryStore[key];
}

export function clear(): void {
  try {
    if (isLocalStorageAvailable()) {
      localStorage.clear();
    }
  } catch {
    // ignore
  }
  Object.keys(memoryStore).forEach(k => delete memoryStore[k]);
}
